// stores/authStore.js - Centralized authentication and profile state
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { SUPPLIER_STAGES } from '@/clubs/manifest.js';
import { EXIT_PENDING_KEY, isReplayOn } from '@/collector/previewReplay.js';
import { AccountStatus } from '@/enums/AccountStatus.enum.js';

const SUPPLIER_STAGE_VALUES = Object.values(SUPPLIER_STAGES);

/**
 * localStorage key used by pinia-plugin-persistedstate for this store.
 * Exported so logout / hard-reset paths can wipe it explicitly without
 * needing to import the store. Keep in sync with the `persist.key` below.
 */
export const AUTH_PERSIST_KEY = 'poolit:auth';

/**
 * Authentication Store - SINGLE SOURCE OF TRUTH for all user data
 *
 * SSOT PRINCIPLE: All user-related data lives here and nowhere else.
 * This includes authentication status, user identity, organization, and membership.
 *
 * STRUCTURE: Mirrors backend API structure exactly for easier maintenance:
 * - user: Basic user identity (id, email, name, telegram)
 * - organization: Business/organization details
 * - membership: Subscription/membership status
 *
 * REACTIVITY: Uses ref() for objects that can be replaced wholesale.
 * Each major entity (user, org, membership) is a separate ref to avoid
 * reactivity loss when updating entire objects from API.
 */
/**
 * Coerce a raw delegation meta block into the canonical inert-by-default shape.
 * A missing/null block (the common case — no impersonation) yields
 * { active: false, actingAs: null, byUser: null }. Kept module-scoped so both
 * the initial ref value and clearSession share one definition.
 */
function normalizeDelegation(raw) {
  return {
    active: raw?.active === true,
    actingAs: raw?.actingAs ?? null,
    byUser: raw?.byUser ?? null,
  };
}

/**
 * Coerce a raw `meta.supplierPreview` block into the canonical inert-by-default
 * shape (supplier-preview P2-A, brief 2026-10-20-supplier-preview): a collector
 * previewing one shop's supplier app reads `{active: true, shop: {id, name,
 * phone}, expiresAt}`; anyone else `{active: false}`. Inactive always carries a
 * null shop, so nothing reads a half-filled block.
 */
/** A live preview was closed with no signal and its DELETE is still owed (supplier-preview-offline L-K). */
function previewExitPending() {
  try {
    return localStorage.getItem(EXIT_PENDING_KEY) != null;
  } catch {
    return false;
  }
}

/** `telegramDm.reachable` values (backend UserResource::telegramReachable()); anything else reads 'unknown'. */
export const TELEGRAM_DM_REACHABLE = Object.freeze(['team-group', 'bot-dm', 'club-account', 'none']);

/** `lastSurface` values (backend UserAppOpen::SURFACES). */
const LAST_SURFACES = Object.freeze(['tg_mini_app', 'browser', 'pwa_installed']);

/**
 * Coerce a raw `telegramDm` attribute (brief 2026-11-03-telegram-dm-surface-
 * deeplink) into `{ reachable, bot, startUrl }`. Absent (an older backend, a
 * session cached before the field) reads `reachable: 'unknown'` with no link,
 * so no surface claims a state the backend never sent.
 */
function normalizeTelegramDm(raw) {
  return {
    reachable: TELEGRAM_DM_REACHABLE.includes(raw?.reachable) ? raw.reachable : 'unknown',
    bot: typeof raw?.bot === 'string' && raw.bot !== '' ? raw.bot : null,
    startUrl: typeof raw?.startUrl === 'string' && raw.startUrl !== '' ? raw.startUrl : null,
  };
}

function normalizeSupplierPreview(raw) {
  const active = raw?.active === true && raw?.shop != null;
  return {
    active,
    shop: active
      ? { id: raw.shop.id ?? null, name: raw.shop.name ?? '', phone: raw.shop.phone ?? null }
      : null,
    expiresAt: active ? (raw.expiresAt ?? null) : null,
  };
}

export const useAuthStore = defineStore('auth', () => {
  // ===== CORE STATE (DOMAIN DATA) =====
  // These mirror the backend API structure exactly

  /**
   * User identity data
   * Replaced wholesale on login/logout
   */
  const user = ref(null); // { id, email, name, telegramUser }

  /**
   * Organization/business data
   * Replaced wholesale when profile updates
   */
  const organization = ref(null); // { id, business_name, phone, address, governorate, status, registration_date }

  /**
   * Membership/subscription data
   * Replaced wholesale when membership changes
   * Shape: { id, status, type, expiry_date, maxBoxesNumber,
   *          code, role, joinedAt, expiresAt, membershipStatus }
   * `status` is the legacy integer (AccountStatus enum, 1-4) — preserved for
   *   back-compat (WalletPage PoolShare gates read it directly).
   * `membershipStatus` is the rich presenter from backend P2-A; null until
   *   that API ships. Composable layer (useMembershipStatus) falls back to the
   *   integer when the presenter is absent.
   */
  const membership = ref(null);

  /**
   * Delegation ("acting as") session-truth — the impersonation banner signal.
   *
   * Sourced from the profile response's RESOURCE-level meta
   * (`data.meta.delegation` server-side; jsona attaches resource meta to the
   * deserialized user object as `.meta`, so it arrives on `user.meta.delegation`
   * — NOT on the document-level `response.data.meta`). Set via setSession's
   * second argument (or the `apiData.meta.delegation` fallback).
   *
   * PERSISTED (in the `pick` list below), so the acting-as banner + its
   * `acting-as-active` content offset paint on the SAME synchronous Priority-2
   * hydration as user/organization/membership, instead of lagging a full
   * /profile round-trip behind the rest of the cached identity (the cold-boot
   * race — owner decision 2026-07-05, Option A). Every act-as enter/exit purges
   * `poolit:auth` + hard-reloads (useActAs.purgeIdentityStorage), so the ONLY
   * way a *stale* delegation is read back is an out-of-band session change while
   * the PWA was closed; the Priority-2 background profile fetch re-derives it and
   * setSession corrects within one round-trip. That failure is a false-PRESENT
   * banner (over-warn, self-healing) — the safe direction for a safety
   * affordance, unlike the false-ABSENT "normal member" window that NOT
   * persisting would leave.
   *
   * Shape: { active: bool, actingAs: {id,name,clubType?,role?}|null,
   *          byUser: {id,name}|null }. Inert default = not impersonating.
   */
  const delegation = ref(normalizeDelegation(null));

  /**
   * The collector's per-shop supplier preview (supplier-preview P2-A) — the
   * resource meta `meta.supplierPreview` of the profile, read exactly like
   * `delegation` (jsona puts it on `user.meta`). While active the profile also
   * says `supplierStage: 'showroom'` and the shop's `supplierOrgId`, so the
   * supplier shell behaves as for a stamped applicant; the four routing checks
   * that must know more read `isSupplierPreview` (design Section 4.2).
   * Persisted with the identity, like the stage, so the banner and the routing
   * paint on the cached boot. Enter and exit purge `poolit:auth` and reload
   * (useSupplierPreview), so a stale value only survives an out-of-band expiry,
   * which the background profile refresh corrects.
   */
  const supplierPreview = ref(normalizeSupplierPreview(null));

  /**
   * «اتسجّل اهتمام المحل»: the lead tap went through during this preview. A
   * page-life flag, not the server's (design Section 4.4) — a reload forgets it
   * and a second tap sends a second, harmless message. Not persisted.
   */
  const supplierPreviewInterestSent = ref(false);

  // ===== UI STATE (TRANSIENT, NOT PERSISTED) =====
  // Only for UI feedback, not part of domain model
  const isLoading = ref(false);
  const error = ref(null);

  // ===== COMPUTED GETTERS =====

  /**
   * Authentication status - derived from user existence
   */
  const isAuthenticated = computed(() => !!user.value?.id);

  /**
   * User has active organization
   */
  const hasOrganization = computed(() => !!organization.value?.id);

  /**
   * User has active membership
   */
  const hasActiveMembership = computed(() =>
    !!membership.value?.id,// membership.value?.status === 'active',
  );

  /**
   * User needs to complete registration
   * True if authenticated but no active membership
   */
  const needsRegistration = computed(() =>
    isAuthenticated.value && !hasActiveMembership.value,
  );

  // ── Membership-status derived helpers ────────────────────────────────────
  // Backward-compat: when membershipStatus (P2-A presenter) is absent, fall
  // back to the legacy integer status. This keeps behaviour identical to today
  // while P2-A is in flight; the day it lands, all gates switch to exact values
  // with zero caller changes.

  function _deriveCanDeposit(m) {
    if (!m) return false;
    if (m.membershipStatus?.canDeposit !== undefined) return !!m.membershipStatus.canDeposit;
    return m.status === AccountStatus.APPROVED;
  }

  function _deriveCanOrder(m) {
    if (!m) return false;
    if (m.membershipStatus?.canOrder !== undefined) return !!m.membershipStatus.canOrder;
    return m.status === AccountStatus.APPROVED;
  }

  /** Key string from the rich presenter, e.g. 'APPROVED_FULL'. Null when absent. */
  const membershipStatusKey = computed(() => membership.value?.membershipStatus?.key ?? null);

  /** accountStatus string from the rich presenter, e.g. 'approved', 'blocked'. Null when absent. */
  const membershipAccountStatus = computed(() => membership.value?.membershipStatus?.accountStatus ?? null);

  /** tier string from the rich presenter, e.g. 'full', 'restricted'. Null when absent. */
  const membershipTier = computed(() => membership.value?.membershipStatus?.tier ?? null);

  /** True if the member is allowed to make deposits right now. */
  const canDeposit = computed(() => _deriveCanDeposit(membership.value));

  /** True if the member is allowed to create order boxes right now. */
  const canOrder = computed(() => _deriveCanOrder(membership.value));

  /** True when accountStatus === 'blocked'. */
  const isMembershipBlocked = computed(() => membershipAccountStatus.value === 'blocked');

  /** True when accountStatus === 'pending'. */
  const isMembershipPending = computed(() => membershipAccountStatus.value === 'pending');

  /** True when the member has been approved but not yet deposited to activate. */
  const isAwaitingDeposit = computed(() => membershipStatusKey.value === 'APPROVED_AWAITING_DEPOSIT');

  /** True when tier === 'full' (fully activated member). */
  const isMembershipFullyActive = computed(() => membershipTier.value === 'full');

  /** True when accountStatus === 'rejected'. */
  const isMembershipRejected = computed(() => membershipAccountStatus.value === 'rejected');

  /**
   * Display name for UI
   */
  const displayName = computed(() =>
    user.value?.name || user.value?.telegramUser?.first_name || 'User',
  );

  /**
   * Telegram user ID (if authenticated via Telegram)
   */
  const telegramUserId = computed(() =>
    user.value?.telegramUser?.id || null,
  );

  /**
   * Has the user linked their Telegram account for the reliable-rail
   * fallback (contract Section 8 — `telegramLinked` in /users/-actions/profile).
   *
   * Distinct from `telegramUserId`: a user may have authed via Telegram OIDC
   * (so `telegramUser` is populated) without having explicitly linked the bot
   * for outbound notifications. Backend gates `telegramLinked` on the
   * notification-link, not the auth-link, so the CTA reads from this signal.
   *
   * Used by the "link Telegram for reliable alerts" CTA on ProfilePage.
   */
  const telegramLinked = computed(() => user.value?.telegramLinked === true);

  /**
   * Is the device's push subscription known-reachable (contract Section 8).
   * Backend flips this to false if the most recent push delivery failed
   * within the last 7 days. Supplemental signal — the primary CTA gate is
   * `telegramLinked === false`. UI may escalate the CTA when both
   * `!pushReachable && !telegramLinked`.
   */
  const pushReachable = computed(() => user.value?.pushReachable === true);

  /**
   * Where an automatic Telegram message for this user lands today —
   * `{ reachable: 'team-group' | 'bot-dm' | 'club-account' | 'none' | 'unknown',
   * bot, startUrl }`. Read off the same resolver the dispatcher uses; `startUrl`
   * opens the assistant bot with the start payload that lets it write to him.
   * 'unknown' when the profile did not carry it (normalizeTelegramDm).
   */
  const telegramDm = computed(() => normalizeTelegramDm(user.value?.telegramDm));

  /** Where he last opened the app — 'tg_mini_app' | 'browser' | 'pwa_installed' | null. */
  const lastSurface = computed(() => user.value?.lastSurface ?? null);

  /**
   * Curated Suppliers-Club supplier flag (Supplier Wall, P7-A). Surfaced ONLY
   * on the profile response (`isSupplier` in /users/-actions/profile) — gate
   * the `/supplier` entry + `requireSupplier` route guard off this. Falsy
   * default so non-supplier and older backend responses simply hide the mode.
   */
  const isSupplier = computed(() => user.value?.isSupplier === true);

  /**
   * The supplier's organization id — emitted for the `showroom` and `active`
   * stages (null for `interested` / `none`), so the shell can name his shop.
   * Like `isSupplier`, present only on the profile path. Not relied upon for
   * the wall request itself (the backend resolves the supplier org server-side
   * from the session).
   */
  const supplierOrgId = computed(() => user.value?.supplierOrgId ?? null);

  /**
   * The Suppliers Club stage (supplier-showroom P2-A): 'none' | 'interested' |
   * 'showroom' | 'active', derived by the backend (`supplierStage` in
   * /users/-actions/profile, brief 2026-10-18-supplier-showroom). Routing reads
   * it through `supplierStageOf` (clubs/manifest.js), which falls back to
   * `isSupplier` for a session cached before the field existed. Default 'none'.
   */
  const supplierStage = computed(() => user.value?.supplierStage ?? 'none');

  /** The shop's owner, the one person who may withdraw from its wallet (the order desk reads). */
  const isShopOwner = computed(() => user.value?.isShopOwner === true);

  /** ISO string once the showroom supplier tapped «اطلب مقابلة شخصية», else null. */
  const supplierMeetingRequestedAt = computed(() => user.value?.supplierMeetingRequestedAt ?? null);

  /** Keep the stamp the meeting endpoint answered with (no profile re-read needed). */
  function setSupplierMeetingRequestedAt(at) {
    if (user.value) user.value = { ...user.value, supplierMeetingRequestedAt: at };
  }

  /**
   * Collector gate (collector-app P1-C). `isCollector` in
   * /users/-actions/profile: an active COLLECTOR or LEADER in an active
   * COLLECTION department, or the admin (backend `User::isCollector()`, brief
   * 2026-09-30-collector-day-read). Arms `requireCollector` and the /collector/
   * surface; it says nothing about a club membership, which a collector may
   * not have. Strict boolean, falsy default — like `isSupplier`.
   */
  const isCollector = computed(() => user.value?.isCollector === true);

  /**
   * Act-as rights gate (delegated-membership access). `true` ⟺ this user is an
   * authorized operator holding ≥1 active act-as grant → the ProfilePage
   * "Act as a member" entry shows. Surfaced on the profile response as
   * `data.attributes.canActAs`, so it rides the same path (and persistence) as
   * `isSupplier`. Is `false` while impersonating (you're the member then), so
   * the entry auto-hides and the banner becomes the only affordance.
   */
  const canActAs = computed(() => user.value?.canActAs === true);

  /** `true` while this session is impersonating a member (act-as active). */
  const isImpersonating = computed(() => delegation.value?.active === true);

  /** The member being acted-as: { id, name, clubType?, role? } | null. */
  const actingAs = computed(() => delegation.value?.actingAs ?? null);

  /** The real operator behind an impersonated session: { id, name } | null. */
  const operator = computed(() => delegation.value?.byUser ?? null);

  /** `true` while this collector previews one shop's supplier app (supplier-preview P2-A). */
  const isSupplierPreview = computed(() => supplierPreview.value?.active === true);

  function markSupplierPreviewInterestSent() {
    supplierPreviewInterestSent.value = true;
  }

  // ===== ACTIONS =====

  /**
   * Set complete user session from API response
   * This is the ONLY way to update auth state
   *
   * @param {Object} apiData - Complete API response with user, organization, membership
   * @param {Object|null} [delegationMeta] - The `meta.delegation` block from the
   *   profile response (the "acting as" banner signal). Falls back to
   *   `apiData.meta.delegation` (jsona attaches resource meta to the user object)
   *   when omitted, so callers that don't thread it explicitly still stay correct.
   */
  function setSession(apiData, delegationMeta = null) {
    if (!apiData?.id) {
      console.error('[AuthStore] Invalid API data - missing user id');
      return;
    }

    // A live preview closed with no signal (L-K): until its DELETE lands the
    // server still answers as the shop. Keep his own identity — the preview
    // stays off. (Not under an offline preview: that profile is the phone's copy.)
    if (previewExitPending() && !isReplayOn()) {
      if (apiData?.meta?.supplierPreview?.active === true) {
        console.warn('[AuthStore] the closed preview is still open on the server — kept off until its exit is sent');
        return;
      }
      try {
        localStorage.removeItem(EXIT_PENDING_KEY); // the server closed it (expiry): nothing owed
      } catch {
        /* noop */
      }
    }

    // Update user data
    user.value = {
      id: apiData.id,
      email: apiData.email || null,
      name: apiData.name || '',
      avatarUrl: apiData.avatarUrl || '',
      status: apiData.status ?? null,
      telegramUser: apiData.telegramUser || null,
      // Notification reliability signals (contract Section 8 — added in P5).
      // `telegramLinked` is the PRIMARY signal for the "link Telegram for
      // reliable alerts" CTA; `pushReachable` is supplemental (false if a
      // push delivery failed in the last 7 days). Both are non-PII flags.
      // Falsy default (false) when omitted so the CTA shows for users on
      // older backend responses lacking the field — better to over-prompt
      // than silently hide the rail UX.
      telegramLinked: apiData.telegramLinked === true,
      pushReachable: apiData.pushReachable === true,
      // The Telegram rail as the dispatcher sees it, and the previous open's
      // surface (pwa-install-notifications P3-A). Profile-path only; absent
      // reads 'unknown' / null.
      telegramDm: normalizeTelegramDm(apiData.telegramDm),
      lastSurface: LAST_SURFACES.includes(apiData.lastSurface) ? apiData.lastSurface : null,
      // Supplier-Wall gate (P7-A). Profile-path only; both default falsy so a
      // non-supplier (or a pre-P7 backend response) keeps the mode hidden.
      isSupplier: apiData.isSupplier === true,
      supplierOrgId: apiData.supplierOrgId ?? null,
      // Suppliers Club stage (supplier-showroom P2-A). Profile-path only; one
      // of the four backend values, anything else (absent, an older backend)
      // is 'none' — routing then falls back to `isSupplier`.
      supplierStage: SUPPLIER_STAGE_VALUES.includes(apiData.supplierStage)
        ? apiData.supplierStage
        : 'none',
      // The moment a showroom supplier asked for the personal meeting (ISO string),
      // null before he asks and at every other stage (supplier-messages P2-A, brief
      // 2026-10-19-supplier-messages). Profile-path only.
      supplierMeetingRequestedAt: apiData.supplierMeetingRequestedAt ?? null,
      // Collector gate (collector-app P1-C). Profile-path only, strict boolean.
      isCollector: apiData.isCollector === true,
      // Act-as rights gate (delegated-membership access). Profile-path only,
      // mirrors isSupplier: strict-boolean so an older backend response (no
      // field) keeps the operator entry hidden. Persisted with `user` so the
      // entry can paint on cold boot before the background refresh confirms.
      canActAs: apiData.canActAs === true,
    };

    // Update organization data (may be null for new users)
    organization.value = apiData.primaryOrganization ? {
      id: apiData.primaryOrganization.id,
      business_name: apiData.primaryOrganization.name || '',
      phone: apiData.primaryOrganization.phone || '',
      address: apiData.primaryOrganization.address || '',
      governorate: apiData.primaryOrganization.state || '',
      status: apiData.primaryOrganization.status || 'active',
      registration_date: apiData.primaryOrganization.registration_date || '',
    } : null;

    // Update membership data (may be null for non-members)
    // New fields (code, role, joinedAt, expiresAt, membershipStatus) default to
    // null so that cold-boot rehydrate from a pre-P2-A localStorage payload
    // never crashes — the composable layer falls back gracefully.
    membership.value = apiData.activeMembership ? {
      id: apiData.activeMembership.id,
      status: apiData.activeMembership.status || null,           // integer, AccountStatus enum — keep unchanged
      type: apiData.activeMembership.type || null,
      expiry_date: apiData.activeMembership.expiry_date || null,
      maxBoxesNumber: apiData.activeMembership.maxBoxesNumber || 0,
      code: apiData.activeMembership.code ?? null,
      role: apiData.activeMembership.role ?? null,
      joinedAt: apiData.activeMembership.joinedAt ?? null,
      expiresAt: apiData.activeMembership.expiresAt ?? null,
      membershipStatus: apiData.activeMembership.membershipStatus ?? null,
    } : null;

    // Delegation ("acting as") signal. Prefer the explicitly-threaded meta;
    // fall back to the resource meta jsona attached to the user object. Coerced
    // to the inert shape when absent (the overwhelmingly common non-operator
    // case) so getters never see a partial object.
    delegation.value = normalizeDelegation(delegationMeta ?? apiData?.meta?.delegation ?? null);

    // The per-shop supplier preview, from the same resource meta. `{active:
    // false}` (or no block at all, an older backend) is the inert shape.
    supplierPreview.value = normalizeSupplierPreview(apiData?.meta?.supplierPreview ?? null);

    error.value = null;

    console.log('[AuthStore] Session set:', {
      userId: user.value.id,
      hasOrg: !!organization.value,
      hasMembership: !!membership.value,
      impersonating: delegation.value.active,
      supplierPreview: supplierPreview.value.active,
    });
  }

  /**
   * Update organization data (after profile edit)
   *
   * @param {Object} orgData - Updated organization data
   */
  function updateOrganization(orgData) {
    if (!isAuthenticated.value) {
      console.warn('[AuthStore] Cannot update organization - not authenticated');
      return;
    }

    organization.value = {
      ...organization.value,
      ...orgData,
    };

    console.log('[AuthStore] Organization updated');
  }

  /**
   * Clear all authentication data (logout)
   */
  function clearSession() {
    user.value = null;
    organization.value = null;
    membership.value = null;
    delegation.value = normalizeDelegation(null);
    supplierPreview.value = normalizeSupplierPreview(null);
    supplierPreviewInterestSent.value = false;
    error.value = null;

    console.log('[AuthStore] Session cleared');
  }

  /**
   * Set loading state
   */
  function setLoading(loading) {
    isLoading.value = loading;
  }

  /**
   * Set error state
   */
  function setError(errorMessage) {
    error.value = errorMessage;
  }

  /**
   * Clear error state
   */
  function clearError() {
    error.value = null;
  }

  return {
    // ===== STATE =====
    user,
    organization,
    membership,
    delegation,
    supplierPreview,
    supplierPreviewInterestSent,
    isLoading,
    error,

    // ===== COMPUTED =====
    isAuthenticated,
    hasOrganization,
    hasActiveMembership,
    needsRegistration,
    displayName,
    telegramUserId,
    telegramLinked,
    pushReachable,
    telegramDm,
    lastSurface,
    // Supplier-Wall gate (P7-A)
    isSupplier,
    supplierOrgId,
    // Suppliers Club stage (supplier-showroom P2-A)
    supplierStage,
    isShopOwner,
    supplierMeetingRequestedAt,
    // Collector gate (collector-app P1-C)
    isCollector,
    // Act-as delegation (impersonation) — canActAs is the operator entry gate;
    // the rest drive the "acting as" banner.
    canActAs,
    isImpersonating,
    actingAs,
    operator,
    // The collector's per-shop supplier preview (supplier-preview P2-A)
    isSupplierPreview,
    // Membership capability flags (F0)
    membershipStatusKey,
    membershipAccountStatus,
    membershipTier,
    canDeposit,
    canOrder,
    isMembershipBlocked,
    isMembershipPending,
    isAwaitingDeposit,
    isMembershipFullyActive,
    isMembershipRejected,

    // ===== ACTIONS =====
    setSession,
    setSupplierMeetingRequestedAt,
    markSupplierPreviewInterestSent,
    updateOrganization,
    clearSession,
    setLoading,
    setError,
    clearError,
  };
}, {
  // ── Persistence ──────────────────────────────────────────────────────────
  // Mirror the user/organization/membership refs to localStorage so the app
  // can render the authenticated UI on cold boot WITHOUT waiting for the
  // /profile API round-trip. `useAuthInitialize()` (Priority 3 in
  // src/composables/useAuth.js) re-validates the session in the background;
  // a 401 hits the auth:unauthorized interceptor and clears state.
  //
  // Invariant: this is a UX cache, NOT the source of truth. The Sanctum
  // session cookie set by the backend is the only thing that authorizes
  // requests. Backend `SESSION_LIFETIME` controls how long that cookie
  // survives — without a long lifetime there, the cached UI flashes and
  // immediately redirects to /login. See BACKEND-TODO comment in
  // src/composables/useAuth.js.
  //
  // `pick` (persistedstate v4 syntax) keeps transient UI state out of
  // storage so we don't restore stale `isLoading: true` flags or last
  // session's error messages.
  //
  // `delegation` is persisted alongside the identity refs (Option A, owner
  // 2026-07-05) so the acting-as banner + `acting-as-active` content offset
  // paint on the SAME synchronous Priority-2 hydration as the rest of the
  // cached identity — closing the cold-boot race where an impersonating
  // operator saw NO acting-as chrome for a full /profile round-trip on reopen,
  // then it popped in (banner + 44px jump). The background refetch re-derives
  // delegation and self-corrects a stale value (see the `delegation` ref above).
  // `supplierPreview` rides along for the same reason (supplier-preview P2-A).
  persist: {
    key: AUTH_PERSIST_KEY,
    storage: localStorage,
    pick: ['user', 'organization', 'membership', 'delegation', 'supplierPreview'],
  },
});
